import './App.css'
import './styles/chartStyles.css'
import { ThemeProvider } from '@mui/material/styles';
import { Box, Container, CssBaseline, Typography, CircularProgress } from '@mui/material';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { I18nProvider } from '@lingui/react';
import  { Trans }  from '@lingui/react/macro';
import { i18n, initI18n, activateLocale, isSupportedLocale } from './i18n';
import SimpleNavigation from './components/SimpleNavigation';
import LocalizedNavigate from './components/LocalizedNavigate';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import retroTheme from './theme/retroTheme';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import CookieConsent from './components/CookieConsent';
import { usePageTracking } from './hooks/usePageTracking';
import { usePerformanceTracking } from './hooks/usePerformanceTracking';

// Lazy load pages
const ServerList = lazy(() => import('./pages/ServerList'));
const Analytics = lazy(() => import('./pages/Analytics'));
const ServerAnalytics = lazy(() => import('./pages/ServerAnalytics'));
const ServerComparison = lazy(() => import('./pages/ServerComparison'));

// Enhanced loading component with retro terminal styling
const PageLoader = () => {
  const theme = retroTheme;
  return (
    <Box
      sx={{
        p: 5,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "70vh", // Ensure it takes enough space but doesn't force scrolling
        width: "100%",
        backgroundImage:
          "linear-gradient(rgba(0, 17, 17, 0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 17, 17, 0.2) 1px, transparent 1px)",
        backgroundSize: "20px 20px",
        backgroundPosition: "0 0",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          mb: 3,
          fontFamily: '"Share Tech Mono", monospace',
          color: theme.palette.primary.main,
          textShadow: `0 0 3px rgba(0, 204, 204, 0.2)`,
          letterSpacing: "1px",
        }}
      >
        <Trans id="loading.initializing">&gt; INITIALIZING SYSTEM</Trans>
      </Typography>
      <CircularProgress
        size={60}
        thickness={4}
        sx={{
          color: theme.palette.primary.main,
          mb: 3,
        }}
      />
      <Typography
        variant="body1"
        sx={{
          fontFamily: '"Share Tech Mono", monospace',
          color: theme.palette.primary.main,
          textShadow: `0 0 2px rgba(0, 204, 204, 0.2)`,
          animation: "blink 1.5s step-end infinite",
          "@keyframes blink": {
            "0%": { opacity: 1 },
            "50%": { opacity: 0.3 },
            "100%": { opacity: 1 },
          },
        }}
      >
        <Trans id="loading.loadingData">&gt; LOADING DATA...</Trans>
      </Typography>
    </Box>
  );
};

function App() {
  // Initialize i18n when the app loads
  useEffect(() => {
    initI18n();
  }, []);

  // Create a component to handle URL-based language detection, page tracking, and performance tracking
  const RouteHandler = ({ children }: { children: React.ReactNode }) => {
    useEffect(() => {
      // Get the current path
      const path = window.location.pathname;

      // Check if the path starts with a language code
      const match = path.match(/^\/([\w-]+)/);
      if (match) {
        const urlLocale = match[1];

        // If it's a supported locale, activate it
        if (isSupportedLocale(urlLocale)) {
          activateLocale(urlLocale);
        }
      }
    }, []);

    // Use the page tracking hook
    usePageTracking();

    // Use the performance tracking hook
    usePerformanceTracking();

    return <>{children}</>;
  };

  return (
    <ThemeProvider theme={retroTheme}>
      <CssBaseline />
      <I18nProvider i18n={i18n}>
        <BrowserRouter>
          <ScrollToTop />
          <AnalyticsProvider>
            <RouteHandler>
              {/* Main layout with flex to ensure footer stays at bottom */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '100vh',
                }}
              >
                {/* Header */}
                <SimpleNavigation />

                {/* Main content - will flex-grow to push footer down */}
                <Box sx={{ flex: '1 0 auto' }}>
                  <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        {/* Default routes (English) */}
                        <Route path="/" element={<ServerList />} />
                        <Route path="/analytics" element={<Analytics />} />
                        <Route path="/compare" element={<ServerComparison />} />
                        <Route path="/server/:serverId" element={<ServerAnalytics />} />

                        {/* Localized routes for pt-BR */}
                        <Route path="/pt-BR" element={<ServerList />} />
                        <Route path="/pt-BR/analytics" element={<Analytics />} />
                        <Route path="/pt-BR/compare" element={<ServerComparison />} />
                        <Route path="/pt-BR/server/:serverId" element={<ServerAnalytics />} />

                        {/* Redirect legacy routes if needed */}
                        <Route path="/en" element={<LocalizedNavigate to="/" replace />} />
                        <Route path="/en/*" element={
                          <Routes>
                            <Route path="analytics" element={<LocalizedNavigate to="/analytics" replace />} />
                            <Route path="compare" element={<LocalizedNavigate to="/compare" replace />} />
                            <Route path="server/:serverId" element={<LocalizedNavigate to="/server/:serverId" replace />} />
                            <Route path="*" element={<LocalizedNavigate to="/" replace />} />
                          </Routes>
                        } />

                        {/* Catch-all route */}
                        <Route path="*" element={<LocalizedNavigate to="/" replace />} />
                      </Routes>
                    </Suspense>
                  </Container>
                </Box>

                {/* Footer - will stay at bottom */}
                <Footer />

                {/* Cookie Consent Banner */}
                <CookieConsent />
              </Box>
            </RouteHandler>
          </AnalyticsProvider>
        </BrowserRouter>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App
